<template>
  <BaseDialog
    :open="open"
    :max-width="'1200px'"
    @close="onClose"
  >
    <template #title>
      {{ headerTitle }}
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <div class="form-row">
        <div class="form-column">
          <div class="field mb-2">
            <label for="tpl-subject">Fach</label>
            <select
              id="tpl-subject"
              v-model.number="model.subject_id"
              class="input"
              :disabled="subjectsLoading"
              required
            >
              <option
                :value="0"
                disabled
              >
                {{ subjectsLoading ? 'Lade Fächer…' : 'Bitte wählen' }}
              </option>
              <option
                v-for="s in subjects"
                :key="s.id"
                :value="s.id"
                :disabled="isSubjectDisabled(s.id)"
              >
                {{
                  (s.abbreviation ? `${s.title} (${s.abbreviation})` : s.title) +
                    (isSubjectDisabled(s.id) ? ' (vorhanden)' : '')
                }}
              </option>
            </select>
          </div>
          <div class="field">
            <label for="tpl-internal-note">interne Notiz</label>
            <input
              id="tpl-internal-note"
              v-model.trim="model.template_json.internal_note"
              class="input"
              maxlength="64"
              title="Nur intern – wird nicht im Lernbrief angezeigt."
            >
          </div>
        </div>

        <div class="field mb-2">
          <label for="tpl-topic">Thema</label>
          <textarea
            id="tpl-topic"
            v-model.trim="model.topic"
            class="input input-textarea-3"
            rows="3"
            maxlength="{{modelTopicMax}}"
            aria-describedby="tpl-topic-count"
            required
            @input="clampTopic"
          />

          <div
            id="tpl-topic-count"
            class="field my-0 text-small"
            role="status"
            aria-live="polite"
            :class="{danger: isTopicOverLimit}"
          >
            {{ modelTopicCount }} / {{ modelTopicMax }} Zeichen
            <span v-if="!isTopicOverLimit && topicRemaining <= 20">
              (noch {{ topicRemaining }})
            </span>
            <span v-else-if="isTopicOverLimit">
              (zu lang – wird gekürzt)
            </span>
          </div>
        </div>
      </div>

      <div class="builder">
        <h3 class="builder-title">
          Rückmeldungen
        </h3>
        <p class="help">
          Hier legst du <strong>Kompetenzgruppen</strong> an und fügst darin <strong>Kompetenzeinträge</strong> hinzu.
        </p>

        <div class="builder-actions">
          <button
            type="button"
            class="btn"
            @click="addGroup"
          >
            + Kompetenzgruppe
          </button>
          <button
            type="button"
            class="btn"
            @click="addBehaviorItem"
          >
            + Verhaltenskriterium
          </button>
        </div>

        <div class="builder-grid">
          <!-- Links: Fachkompetenz (gruppiert) -->
          <section class="panel">
            <header class="panel-header">
              <h4 class="panel-title">
                Kompetenzen
              </h4>
              <span class="meta">{{ groupedSection.groups.length }} Gruppen</span>
            </header>

            <div
              v-if="groupedSection.groups.length === 0"
              class="empty"
            >
              Noch keine Gruppen. Klicke auf „Kompetenzgruppe“.
            </div>

            <article
              v-for="(g, gIdx) in groupedSection.groups"
              :key="g.id"
              class="group"
            >
              <div class="group-head">
                <div class="field mb-0">
                  <label :for="`grp-title-${g.id}`">Gruppentitel</label>
                  <input
                    :id="`grp-title-${g.id}`"
                    v-model.trim="g.title"
                    class="input"
                    type="text"
                    placeholder="z. B. Flächen und Körper"
                    required
                  >
                </div>

                <div class="field mb-0 group-actions">
                  <button
                    type="button"
                    class="btn"
                    @click="addCompetencyItem(gIdx)"
                  >
                    + Kompetenz
                  </button>
                  <button
                    class="btn danger icon-only"
                    type="button"
                    title="Gruppe löschen"
                    aria-label="Gruppe löschen"
                    @click="removeGroup(gIdx)"
                  >
                    <i
                      class="pi pi-trash"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>

              <div class="items">
                <div
                  v-if="g.items.length === 0"
                  class="empty small"
                >
                  Noch keine Einträge in dieser Gruppe.
                </div>

                <div
                  v-for="(it, itIdx) in g.items"
                  :key="it.id"
                  class="item-row"
                >
                  <div class="field mb-0">
                    <input
                      :id="`comp-label-${it.id}`"
                      v-model.trim="it.label"
                      class="input"
                      type="text"
                      placeholder="z. B. Größen umwandeln"
                      required
                    >
                  </div>

                  <button
                    class="btn danger icon-only"
                    type="button"
                    title="Kompetenz löschen"
                    aria-label="Kompetenz löschen"
                    @click="removeCompetencyItem(gIdx, itIdx)"
                  >
                    <i
                      class="pi pi-trash"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </article>
          </section>

          <!-- Rechts: Arbeits- und Sozialverhalten -->
          <section class="panel">
            <header class="panel-header">
              <h4 class="panel-title">
                Arbeits- und Sozialverhalten
              </h4>
              <span class="meta">{{ behaviorSection.items.length }} Kriterien</span>
            </header>

            <div
              v-if="behaviorSection.items.length === 0"
              class="empty"
            >
              Noch keine Kriterien. Klicke auf „Verhaltenskriterium“.
            </div>

            <article class="group behavior-items">
              <div
                v-for="(it, idx) in behaviorSection.items"
                :key="it.id"
                class="item-row"
              >
                <div class="field mb-0">
                  <input
                    :id="`beh-label-${it.id}`"
                    v-model.trim="it.label"
                    class="input"
                    type="text"
                    placeholder="z. B. Teamfähigkeit"
                    required
                  >
                </div>

                <button
                  class="btn danger icon-only"
                  type="button"
                  title="Verhalten löschen"
                  aria-label="Verhalten löschen"
                  @click="removeBehaviorItem(idx)"
                >
                  <i
                    class="pi pi-trash"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </article>
          </section>
        </div>
      </div>

      <details
        class="raw d-none"
        open
      >
        <summary class="raw-summary">
          Template JSON (optional manuell bearbeiten)
        </summary>

        <div class="field">
          <label for="tpl-json">Template JSON</label>
          <textarea
            id="tpl-json"
            v-model="jsonText"
            class="textarea"
            rows="12"
            spellcheck="false"
            @blur="applyJsonTextToModel"
          />
          <p class="help">
            Tipp: Nach manuellen Änderungen Feld verlassen (Blur), dann wird die Vorlage oben neu aufgebaut.
          </p>
          <p
            v-if="jsonError"
            class="help danger"
            role="alert"
          >
            {{ jsonError }}
          </p>
        </div>
      </details>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div class="actions">
        <div class="actions-left">
          <span
            v-if="pdfFitHint.show"
            class="badge badge-warn font-bold"
            :title="pdfFitHint.text"
            role="status"
            style="font-size: 13px"
            aria-label="Überlängen-Hinweis"
          >
            {{ pdfFitHint.text }}
          </span>
        </div>

        <div class="actions-right">
          <button
            type="button"
            class="btn"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="saving"
          >
            {{ saving ? 'Speichern…' : 'Speichern' }}
          </button>
        </div>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {useAuthStore} from '@/stores/auth';
import api from '@/services/api';
import {computeTemplatePdfFit} from '@/util/templatePdfFit';

const props = defineProps({
  open: {type: Boolean, default: false},
  mode: {type: String, default: 'new'}, // 'new' | 'edit' | 'duplicate'
  initial: {type: Object, default: null},
  // Optional: Fächer aus dem aktuell ausgewählten Lernbrief
  allowedSubjects: {type: Array, default: () => []},
  // Optional: bereits belegte Fach-IDs im Lernbrief (eine Vorlage je Fach)
  usedSubjectIds: {type: Array, default: () => []},
});

const modelTopicMax = 245;

const modelTopicCount = computed(() => {
  const v = String(model.value.topic ?? '');
  return v.length;
});

const topicRemaining = computed(() => {
  return Math.max(0, modelTopicMax - modelTopicCount.value);
});

const isTopicOverLimit = computed(() => modelTopicCount.value > modelTopicMax);

function clampTopic() {
  const v = String(model.value.topic ?? '');
  if (v.length <= modelTopicMax) return;
  model.value.topic = v.slice(0, modelTopicMax);
}

function getSelectedSubjectTitle() {
  const sid = Number(model.value.subject_id) || 0;
  if (!sid) return '';
  const s = (Array.isArray(subjects.value) ? subjects.value : []).find(x => Number(x?.id) === sid);
  return String(s?.title || '').trim();
}

const pdfFitHint = computed(() => {
  return computeTemplatePdfFit({
    templateJson: model.value.template_json,
    subjectTitle: getSelectedSubjectTitle(),
    topic: model.value.topic,
  });
});

const emit = defineEmits(['close', 'saved']);
const auth = useAuthStore();
const subjects = ref([]);
const subjectsLoading = ref(false);
const subjectsError = ref(null);

const isAdminLike = computed(() => {
  if (auth.isAdmin) return true;
  const perms = Array.isArray(auth.user?.permissions) ? auth.user.permissions : [];
  return perms.includes('feedback_letters_manage');
});

function isSubjectDisabled(sid) {
  const id = Number(sid);
  if (!Number.isFinite(id) || id <= 0) return true;

  // Mehrere Vorlagen je Fach sind erlaubt:
  // Im Dropdown soll ein Fach daher NICHT wegen "vorhanden" deaktiviert werden.
  return false;
}

async function loadSubjects() {
  subjectsLoading.value = true;
  subjectsError.value = null;
  try {
    // 1) Basisliste: alle Fächer
    const allRes = await api.get('/feedback-subjects');
    const allItems = Array.isArray(allRes?.data?.items) ? allRes.data.items : (Array.isArray(allRes?.data) ? allRes.data : []);

    // 2) Wenn Lehrer UND NICHT admin-like → eigene Fächer abrufen und schneiden
    const roleId = Number(auth.user?.roleId ?? auth.user?.role_id ?? 0);
    let mine = null;
    if (roleId === 2 && !isAdminLike.value) {
      try {
        const mineRes = await api.get('/feedback-subjects/mine');
        mine = Array.isArray(mineRes?.data?.items) ? mineRes.data.items : (Array.isArray(mineRes?.data) ? mineRes.data : []);
      } catch {
        mine = null; // Fallback: alle
      }
    }

    // 3) Falls allowedSubjects (vom Lernbrief) übergeben wurde → ebenfalls schneiden
    const allowed = Array.isArray(props.allowedSubjects) ? props.allowedSubjects : [];

    // Hilfsmenge: erlaubt durch Lehrer-Zuordnung
    const mineSet = mine ? new Set(mine.map(s => Number(s?.id)).filter(n => Number.isFinite(n) && n > 0)) : null;
    // Hilfsmenge: erlaubt durch aktuellen Lernbrief
    const allowedSet = allowed.length
        ? new Set(allowed.map(s => Number(s?.id)).filter(n => Number.isFinite(n) && n > 0))
        : null;

    let list = allItems.filter(s => Number.isFinite(Number(s?.id)) && Number(s?.id) > 0);

    if (mineSet) {
      list = list.filter(s => mineSet.has(Number(s.id)));
    }
    if (allowedSet) {
      list = list.filter(s => allowedSet.has(Number(s.id)));
    }

    const isEdit = props.mode === 'edit';
    const currentId = Number(isEdit ? (props.initial?.subject_id ?? 0) : 0) || 0;

    if (currentId > 0 && !list.some(s => Number(s.id) === currentId)) {
      // Versuche, aus allowed/mine/allItems zu finden, sonst Minimalobjekt anhängen
      const src = [...allowed, ...(mine || []), ...allItems];
      const cur = src.find(s => Number(s?.id) === currentId) || {id: currentId, title: '(aktuelles Fach)'};
      list = [cur, ...list];
    }

    subjects.value = list
        .slice()
        .sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'de', {sensitivity: 'base'}));
  } catch (e) {
    subjects.value = [];
    subjectsError.value = e?.message || 'Fächer konnten nicht geladen werden';
  } finally {
    subjectsLoading.value = false;
  }
}

const saving = ref(false);
const error = ref(null);
const jsonError = ref(null);
const suspendJsonSync = ref(false);

function createId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function defaultTemplateJson() {
  return {
    title: 'Rückmeldungsbogen',
    internal_note: '',
    sections: [
      {
        key: 'fachkompetenz',
        label: 'Fachkompetenz',
        type: 'grouped_competencies',
        groups: [],
      },
      {
        key: 'verhalten',
        label: 'Arbeits- und Sozialverhalten',
        type: 'behavior_items',
        items: [],
      },
    ],
  };
}

function normalizeTemplateJson(input, remapIds = false) {
  const base = defaultTemplateJson();

  // NEU: template_json kann als String (JSON) vom Backend kommen
  let parsedInput = input;
  if (typeof parsedInput === 'string') {
    try {
      parsedInput = JSON.parse(parsedInput);
    } catch (e) {
      parsedInput = null;
    }
  }

  const tpl = (parsedInput && typeof parsedInput === 'object' && !Array.isArray(parsedInput)) ? parsedInput : {};
  const sections = Array.isArray(tpl.sections) ? tpl.sections : base.sections;
  const grouped =
      sections.find(s => s?.type === 'grouped_competencies' || s?.key === 'fachkompetenz') || base.sections[0];
  const behavior =
      sections.find(s => s?.type === 'behavior_items' || s?.key === 'verhalten') || base.sections[1];
  const groupedSection = {
    key: grouped.key ?? 'fachkompetenz',
    label: grouped.label ?? 'Fachkompetenz',
    type: 'grouped_competencies',
    groups: Array.isArray(grouped.groups) ? grouped.groups : [],
  };

  groupedSection.groups = groupedSection.groups.map(g => ({
    id: remapIds ? createId('group') : (g?.id || createId('group')),
    title: (g?.title ?? '').toString(),
    items: Array.isArray(g?.items)
        ? g.items.map(it => ({
          id: remapIds ? createId('comp') : (it?.id || createId('comp')),
          label: (it?.label ?? '').toString(),
        }))
        : [],
  }));

  const behaviorSection = {
    key: behavior.key ?? 'verhalten',
    label: behavior.label ?? 'Arbeits- und Sozialverhalten',
    type: 'behavior_items',
    items: Array.isArray(behavior.items) ? behavior.items : [],
  };

  behaviorSection.items = behaviorSection.items.map(it => ({
    id: remapIds ? createId('beh') : (it?.id || createId('beh')),
    label: (it?.label ?? '').toString(),
  }));

  return {
    title: (tpl.title ?? base.title).toString(),
    internal_note: String(tpl.internal_note ?? base.internal_note ?? '').toString(),
    sections: [groupedSection, behaviorSection],
  };
}

function makeDuplicateTopic(topicOrTitle) {
  const base = String(topicOrTitle ?? '').trim();
  if (!base) return '';
  if (/\(Kopie\)\s*$/i.test(base)) return base;
  return `${base} (Kopie)`;
}

function seedModelFromInitialAsNew(initial) {
  const topic = initial?.topic ?? initial?.title ?? '';
  return {
    id: null,
    user_id: auth.user?.id ?? null,
    topic: makeDuplicateTopic(topic),
    subject_id: initial?.subject_id ?? null,
    feedback_letter_id: initial?.feedback_letter_id ?? null,
    template_json: normalizeTemplateJson(initial?.template_json, true),
  };
}

const model = ref({
  id: null,
  user_id: null,
  topic: '',
  subject_id: null,
  feedback_letter_id: null,
  template_json: defaultTemplateJson(),
});

const groupedSection = computed(() => {
  const sec = model.value.template_json.sections.find(s => s.type === 'grouped_competencies');
  return sec || model.value.template_json.sections[0];
});

const behaviorSection = computed(() => {
  const sec = model.value.template_json.sections.find(s => s.type === 'behavior_items');
  return sec || model.value.template_json.sections[1];
});

const jsonText = ref('');

const headerTitle = computed(() => {
  if (props.mode === 'edit') return 'Vorlage bearbeiten';
  if (props.mode === 'duplicate') return 'Vorlage duplizieren';
  return 'Vorlage hinzufügen';
});

watch(
    () => props.open,
    async (isOpen) => {
      if (!isOpen) return;

      // Wenn Fächer aus dem Lernbrief übergeben wurden → diese verwenden, sonst global laden
      error.value = null;
      jsonError.value = null;

      await loadSubjects();

      if (props.mode === 'edit' && props.initial) {
        model.value = {
          id: props.initial.id ?? null,
          user_id: props.initial.user_id ?? null,
          topic: props.initial.topic ?? props.initial.title ?? '',
          subject_id: props.initial.subject_id ?? null,
          feedback_letter_id: props.initial.feedback_letter_id ?? null,
          template_json: normalizeTemplateJson(props.initial.template_json, false),
        };
      } else if ((props.mode === 'duplicate' || props.mode === 'new') && props.initial) {
        model.value = seedModelFromInitialAsNew(props.initial);
      } else {
        model.value = {
          id: null,
          user_id: auth.user?.id ?? null,
          topic: '',
          subject_id: null,
          feedback_letter_id: null,
          template_json: defaultTemplateJson(),
        };
      }

      jsonText.value = JSON.stringify(model.value.template_json, null, 2);
    },
    {immediate: true}
);

// Wenn oben im Builder editiert wird, JSON automatisch mitziehen
watch(
    () => model.value.template_json,
    (val) => {
      if (suspendJsonSync.value) return;
      jsonError.value = null;
      jsonText.value = JSON.stringify(val, null, 2);
    },
    {deep: true}
);

function applyJsonTextToModel() {
  jsonError.value = null;

  let parsed;
  try {
    parsed = JSON.parse(jsonText.value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Template JSON muss ein Objekt sein.');
    }
  } catch (e) {
    jsonError.value = e?.message || 'Ungültiges JSON';
    return;
  }

  suspendJsonSync.value = true;
  try {
    model.value.template_json = normalizeTemplateJson(parsed);
    jsonText.value = JSON.stringify(model.value.template_json, null, 2);
  } finally {
    suspendJsonSync.value = false;
  }
}

// --- Builder Aktionen (oben)
function addGroup() {
  groupedSection.value.groups.push({
    id: createId('group'),
    title: '',
    items: [],
  });
}

function removeGroup(groupIndex) {
  groupedSection.value.groups.splice(groupIndex, 1);
}

function addCompetencyItem(groupIndex) {
  const g = groupedSection.value.groups[groupIndex];
  if (!g) return;
  g.items.push({
    id: createId('comp'),
    label: '',
  });
}

function removeCompetencyItem(groupIndex, itemIndex) {
  const g = groupedSection.value.groups[groupIndex];
  if (!g) return;
  g.items.splice(itemIndex, 1);
}

function addBehaviorItem() {
  behaviorSection.value.items.push({
    id: createId('beh'),
    label: '',
  });
}

function removeBehaviorItem(index) {
  behaviorSection.value.items.splice(index, 1);
}

function onClose() {
  emit('close');
}

async function onSubmit() {
  error.value = null;

  // Wenn jemand im JSON-Feld war: einmal versuchen zu übernehmen, wenn es valide ist
  // (Bei Fehler blockieren wir nicht hart, solange jsonError existiert, damit Builder weiter nutzbar bleibt.)
  if (jsonText.value?.trim()) {
    const prev = jsonError.value;
    applyJsonTextToModel();
    if (jsonError.value && jsonError.value !== prev) {
      // JSON ist kaputt → Speichern verhindern, damit wir nicht unerwartet etwas speichern
      error.value = jsonError.value;
      return;
    }
  }

  const tpl = normalizeTemplateJson(model.value.template_json);

  // Minimale fachliche Validierung: Gruppen müssen existieren und jede Gruppe braucht Titel
  if (!tpl.sections[0].groups.length) {
    error.value = 'Bitte mindestens eine Kompetenzgruppe anlegen.';
    return;
  }
  const hasEmptyGroupTitle = tpl.sections[0].groups.some(g => !String(g.title || '').trim());
  if (hasEmptyGroupTitle) {
    error.value = 'Bitte jeder Kompetenzgruppe einen Titel geben.';
    return;
  }

  // subject_id bestimmen (Fallback: falls noch alter String vorhanden sein sollte)
  let subjectId = Number(model.value.subject_id) || 0;
  if (!subjectId && model.value.subject) {
    const match = (Array.isArray(subjects.value) ? subjects.value : []).find(s => String(s.title) === String(model.value.subject));
    if (match && Number(match.id)) {
      subjectId = Number(match.id);
    }
  }

  // Mehrere Vorlagen je Fach je Lernbrief sind erlaubt → keine Client-Sperre mehr anhand usedSubjectIds.

  const payload = {
    user_id: model.value.user_id ?? (auth.user?.id ?? null),
    topic: model.value.topic,
    subject_id: subjectId > 0 ? subjectId : null,
    feedback_letter_id: Number(model.value.feedback_letter_id) > 0 ? Number(model.value.feedback_letter_id) : null,
    template_json: tpl,
  };

  saving.value = true;
  try {
    emit('saved', {
      mode: props.mode,
      id: model.value.id,
      payload,
      pdfIsOverLimit: Boolean(pdfFitHint.value?.isOverLimit),
    });
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 82vh;
  max-height: 82vh;
  overflow: hidden; /* Scroll nur innerhalb .builder */
}

.field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 2px;
}

.input,
.textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px;
  background: var(--surface);
}

.input-textarea-3 {
  resize: none;
  max-height: 85px;
  min-height: 85px;
}

#tpl-topic-count.text-small {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin: 4px 0 0 0;
  text-align: right;
}

#tpl-topic-count.text-small.danger {
  color: var(--danger);
}

.actions {
  display: flex;
  justify-content: space-between; /* Warn-Badge links, Buttons rechts */
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.actions-right {
  display: inline-flex;
  gap: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
}

.badge-warn {
  border-color: color-mix(in srgb, var(--warn) 55%, var(--border));
  background: color-mix(in srgb, var(--warn) 10%, var(--surface));
  color: var(--warn);
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.btn.danger {
  border-color: color-mix(in srgb, var(--danger) 60%, var(--border));
  color: var(--danger);
}

.builder {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  background: var(--surface);
  flex: 1 1 auto;
  overflow-y: auto; /* vertikales Scrollen bei Überhöhe */
}

.builder-title {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.builder-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  justify-content: space-between;
}

.builder-grid {
  display: grid;
  grid-template-columns: 1.2fr .8fr;
  gap: 12px;
  margin-top: 12px;
}

.builder-grid input, .builder-grid button {
  height: 28px;
}

.panel {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}

.panel-title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}

.meta {
  color: var(--muted);
  font-size: 12px;
}

.group {
  padding: 12px;
  border-bottom: 1px solid var(--border);
}

.group:last-child {
  border-bottom: none;
}

.group-head {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: end;
}

.group-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.items {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 25px;
}

.item-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: end;
}

.group.behavior-items .item-row {
  margin-top: 6px;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin: 0;
}

.help.danger {
  color: var(--danger);
}

.empty {
  padding: 12px;
  color: var(--muted);
  font-size: 12px;
}

.empty.small {
  padding: 8px 0 0 0;
}

/* Icon-Button Variante (PrimeIcons) */
.btn.icon-only {
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn.icon-only .pi {
  pointer-events: none;
}

.raw {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--surface);
}

.raw-summary {
  cursor: pointer;
  color: var(--text);
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 260px auto;
  gap: 12px;
}

:deep(footer) {
  display: none;
}

@media (max-width: 1024px) {
  .builder-grid {
    grid-template-columns: 1fr;
  }
}
</style>